import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuditLogs } from '../../store/slices/auditSlice';
import { fetchUsers } from '../../store/slices/usersSlice';
import apiService from '../../services/api';
import { subscribeToUsersUpdates } from '../../services/socket';
import {
  FileText, User, X, Eye, Download,
  Plus, Trash2, LayoutDashboard, RotateCcw,
  CheckCircle, Settings, AlertTriangle, Loader2,
} from 'lucide-react';
import { exportAuditToExcel } from '../../utils/excelExport';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/format';
import { formatProductUnitType } from '../../utils/unitLabels';
import {
  classifyFieldChange,
  getStoreCollectionItemChanges,
  getStoreFieldSummary,
  getStoreCollectionItemDisplay,
  formatStoreAuditScalarValue,
  isStoreCollectionField,
  getStoreRemovedMessageKey,
  getStoreAddedMessageKey,
  STORE_ITEM_COLLECTION_FIELDS,
  normalizeHexColor,
  valuesEqual,
  toCalendarDateDisplay,
  formatCalendarDateDisplay,
  CALENDAR_DATE_AUDIT_FIELDS,
} from '../../utils/auditDisplay';
import { getTokens } from '../../utils/tokens';
import {
  PageHeader, GhostBtn, Pagination,
} from '../ui/AdminPagePrimitives';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function formatDayLabel(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

function getDayKey(ts) {
  if (!ts) return 'unknown';
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function getKindIcon(action = '') {
  const a = action.toLowerCase();
  if (a.includes('creat') || a.includes('add') || a.includes('open')) return <Plus size={13} />;
  if (a.includes('updat') || a.includes('edit') || a.includes('modif')) return <Settings size={13} />;
  if (a.includes('delet') || a.includes('remov')) return <Trash2 size={13} />;
  if (a.includes('login') || a.includes('logout') || a.includes('auth')) return <User size={13} />;
  if (a.includes('session') || a.includes('clos')) return <LayoutDashboard size={13} />;
  if (a.includes('refund')) return <RotateCcw size={13} />;
  if (a.includes('approv') || a.includes('review')) return <CheckCircle size={13} />;
  if (a.includes('alert') || a.includes('warn')) return <AlertTriangle size={13} />;
  return <FileText size={13} />;
}

function getKindColor(action = '', tk) {
  const a = action.toLowerCase();
  if (a.includes('creat') || a.includes('add') || a.includes('open')) return tk.accentText;
  if (a.includes('approv') || a.includes('review')) return tk.successText;
  if (a.includes('updat') || a.includes('edit')) return tk.warning;
  if (a.includes('delet') || a.includes('remov')) return tk.dangerText;
  if (a.includes('alert') || a.includes('warn')) return tk.dangerText;
  return tk.textSecondary;
}

// ─── Skeleton rows ─────────────────────────────────────────────────────────────

function SkeletonRow({ tk }) {
  const shimmer = {
    background: `linear-gradient(90deg, ${tk.surfaceAlt} 0%, ${tk.border} 50%, ${tk.surfaceAlt} 100%)`,
    backgroundSize: '200% 100%',
    animation: 'auditShimmer 1.4s ease-in-out infinite',
    borderRadius: 5,
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '52px 32px 1fr 96px 32px', alignItems: 'center', gap: '0 12px', padding: '13px 16px' }}>
      <div style={{ ...shimmer, height: 11, width: 36 }} />
      <div style={{ ...shimmer, width: 32, height: 32, borderRadius: 8 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ ...shimmer, height: 11, width: '55%' }} />
        <div style={{ ...shimmer, height: 10, width: '35%' }} />
      </div>
      <div style={{ ...shimmer, height: 20, borderRadius: 5 }} />
      <div style={{ ...shimmer, width: 32, height: 32, borderRadius: 8 }} />
    </div>
  );
}

// ─── Audit component ──────────────────────────────────────────────────────────

const Audit = () => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.theme);
  const { list, loading, error } = useSelector((state) => state.audit);
  const { list: users } = useSelector((state) => state.users);
  const { t, isRTL } = useLanguage();
  const tk = getTokens(mode);
  const { language } = useLanguage();

  const [actionFilter, setActionFilter] = useState('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewModal, setPreviewModal] = useState({ open: false, log: null });
  const [refundDetails, setRefundDetails] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [exporting, setExporting] = useState(false);

  const wrapperStyle = {
    background: tk.bg,
    color: tk.text,
    padding: 20,
    minHeight: '100%',
    fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };

  const dateInput = {
    height: 32,
    padding: '0 8px',
    background: tk.surface,
    border: `1px solid ${tk.border}`,
    borderRadius: 7,
    color: tk.text,
    fontFamily: 'inherit',
    fontSize: 12,
    outline: 'none',
    cursor: 'pointer',
    minWidth: 0,
  };

  useEffect(() => {
    const unsubscribe = subscribeToUsersUpdates(() => {
      dispatch(fetchUsers(true)).catch(() => {});
    });
    return unsubscribe;
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAuditLogs({
      action: actionFilter === 'all' ? undefined : actionFilter,
      entity_type: entityTypeFilter === 'all' ? undefined : entityTypeFilter,
      actor_id: actorFilter === 'all' ? undefined : actorFilter,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    }));
  }, [dispatch, actionFilter, entityTypeFilter, actorFilter, startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [actionFilter, entityTypeFilter, actorFilter, startDate, endDate]);

  const filteredList = React.useMemo(() => list || [], [list]);

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const paginatedEvents = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredList.slice(start, start + itemsPerPage);
  }, [filteredList, currentPage, itemsPerPage]);

  const hasActiveFilters =
    actionFilter !== 'all' ||
    entityTypeFilter !== 'all' ||
    actorFilter !== 'all' ||
    !!startDate ||
    !!endDate;

  const clearFilters = () => {
    setActionFilter('all');
    setEntityTypeFilter('all');
    setActorFilter('all');
    setStartDate('');
    setEndDate('');
  };

  const actionToKey = (action) => {
    if (!action) return '';
    return action.split('_').map((part, i) => i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)).join('');
  };

  const entityTypeToKey = (entityType) => {
    if (!entityType) return '';
    return 'entity' + entityType.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('');
  };

  const getActionLabel = (action) => {
    const key = actionToKey(action);
    const translated = key ? t(`audit.${key}`) : '';
    return translated || action?.replace(/_/g, ' ') || action;
  };

  const getEntityTypeLabel = (entityType) => {
    const key = entityTypeToKey(entityType);
    const translated = key ? t(`audit.${key}`) : '';
    return translated || entityType || '';
  };

  const getRoleLabel = (role) => {
    if (!role) return '';
    const r = String(role).toLowerCase();
    if (r === 'owner') return t('audit.roleOwner');
    if (r === 'admin') return t('audit.roleAdmin');
    if (r === 'manager') return t('audit.roleManager');
    if (r === 'cashier') return t('audit.roleCashier');
    return role;
  };

  const formatJSON = (data) => {
    if (!data) return null;
    try { return typeof data === 'string' ? JSON.parse(data) : data; } catch { return null; }
  };

  const getFieldLabel = (key) => {
    if (key === 'color') return t('inventory.color');
    if (key === 'session_number') return t('sessions.sessionNo');
    if (key === 'register_name') return t('sessions.register');
    if (key === 'license_slot_id') return t('settings.licenseSlotsColSlot');
    if (key === 'opening_cash') return t('sessions.openingCash');
    if (key === 'unit_selling_price') return t('inventory.sellingPrice');
    if (key === 'unit_price') return t('inventory.costPrice');
    if (key === 'total_price') return t('common.total');
    if (key === 'quantity') return t('common.quantity');
    if (key === 'purchased_at') return t('common.date');
    if (key === 'old_inventory') return t('inventory.stockQuantity');
    if (key === 'old_cost_price') return t('inventory.costPrice');
    if (key === 'old_selling_price') return t('inventory.sellingPrice');
    if (key === 'preview_cost') return t('offers.previewCost');
    if (key === 'image_path') return t('inventory.image');
    if (key === 'unit_type') return t('inventory.unitType');
    const translated = t(`audit.fields.${key}`);
    return translated !== `audit.fields.${key}` ? translated : key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const hiddenFields = ['id', 'created_at', 'updated_at', 'actor_id', 'entity_id', 'category_id', 'supplier_id', 'user_id', 'session_id', 'order_id', 'refund_id', 'register_id', 'product_id', 'items', 'preview_cost', 'offer_id'];

  const PURCHASE_MONEY_FIELDS = new Set(['unit_price', 'unit_selling_price', 'total_price', 'old_cost_price', 'old_selling_price', 'selling_price', 'preview_cost']);
  const PURCHASE_QTY_FIELDS = new Set(['quantity', 'old_inventory']);

  const getUserAuditName = (log) => {
    const details = formatJSON(log.details);
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    return details?.full_name || details?.username || newVal?.full_name || newVal?.username || oldVal?.full_name || oldVal?.username || '';
  };

  const getUserUpdatedSummary = (log, actor) => {
    const details = formatJSON(log.details);
    const name = getUserAuditName(log);
    if (details?.event === 'deactivated') {
      return t('audit.summary.userDeactivated', { actor, name });
    }
    const statusChange = details?.changes?.find((change) => change.field === 'status');
    if (statusChange?.to === true) {
      return t('audit.summary.userReactivated', { actor, name });
    }
    if (details?.changes?.some((change) => change.field === 'password_changed')) {
      return t('audit.summary.userPasswordReset', { actor, name });
    }
    return t('audit.summary.userUpdated', { actor, name });
  };

  const getUserUpdatedSummaryNoActor = (log) => {
    const details = formatJSON(log.details);
    const name = getUserAuditName(log);
    if (details?.event === 'deactivated') {
      return t('audit.summaryNoActor.userDeactivated', { name });
    }
    const statusChange = details?.changes?.find((change) => change.field === 'status');
    if (statusChange?.to === true) {
      return t('audit.summaryNoActor.userReactivated', { name });
    }
    if (details?.changes?.some((change) => change.field === 'password_changed')) {
      return t('audit.summaryNoActor.userPasswordReset', { name });
    }
    return t('audit.summaryNoActor.userUpdated', { name });
  };

  const formatUserStatus = (value) => {
    if (value === true || value === 'true') return t('settings.active');
    if (value === false || value === 'false') return t('settings.inactive');
    return '—';
  };

  const isTruthyActive = (value) => value !== false && value !== 'false' && value !== 0;

  const renderActiveStatusBadge = (value, { strikethrough = false } = {}) => {
    const active = isTruthyActive(value);
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 5,
          fontSize: 10,
          fontWeight: 700,
          background: active ? tk.successSoft : tk.dangerSoft,
          color: active ? tk.successText : tk.dangerText,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          ...(strikethrough ? { textDecoration: 'line-through', opacity: 0.9 } : {}),
        }}
      >
        {active ? t('settings.active') : t('settings.inactive')}
      </span>
    );
  };

  const formatUserAuditFieldValue = (field, value) => {
    if (value === null || value === undefined) return '—';
    if (field === 'status') return formatUserStatus(value);
    if (field === 'role') return getRoleLabel(value);
    if (field === 'password_changed') return value ? t('audit.userAudit.passwordReset') : '—';
    return renderValue(value, true);
  };

  const getReadableSummary = (log) => {
    const actor = log.actor_name || t('common.system');
    const details = formatJSON(log.details);
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    const itemName = details?.name || details?.product_name || newVal?.name || oldVal?.name || '';
    const formatMoney = (val) => `${t('currency.egp')} ${Number(val).toFixed(2)}`;
    switch (log.action) {
      case 'product_created': return t('audit.summary.productCreated', { actor, name: itemName || t('common.product') });
      case 'product_updated': return t('audit.summary.productUpdated', { actor, name: itemName || t('common.product') });
      case 'product_deleted': return t('audit.summary.productDeleted', { actor, name: itemName || t('common.product') });
      case 'session_opened': return t('audit.summary.sessionOpened', { actor, amount: details?.opening_cash || newVal?.opening_cash ? formatMoney(details?.opening_cash || newVal?.opening_cash) : '' });
      case 'session_closed': return t('audit.summary.sessionClosed', { actor, sales: details?.total_sales || newVal?.total_sales ? formatMoney(details?.total_sales || newVal?.total_sales) : '' });
      case 'session_force_closed': return t('audit.summary.sessionForceClosed', { actor });
      case 'order_refunded': return t('audit.summary.orderRefunded', { actor, amount: details?.amount || details?.refund_amount ? formatMoney(details?.amount || details?.refund_amount) : '' });
      case 'order_partially_refunded': return t('audit.summary.orderPartiallyRefunded', { actor, amount: details?.amount || details?.refund_amount ? formatMoney(details?.amount || details?.refund_amount) : '' });
      case 'category_created': return t('audit.summary.categoryCreated', { actor, name: itemName || t('common.category') });
      case 'category_updated': return t('audit.summary.categoryUpdated', { actor, name: itemName || t('common.category') });
      case 'category_deleted': return t('audit.summary.categoryDeleted', { actor, name: itemName || t('common.category') });
      case 'offer_created': return t('audit.summary.offerCreated', { actor, name: itemName || t('nav.offers') });
      case 'offer_updated': return t('audit.summary.offerUpdated', { actor, name: itemName || t('nav.offers') });
      case 'offer_deleted': return t('audit.summary.offerDeleted', { actor, name: itemName || t('nav.offers') });
      case 'expense_created': return t('audit.summary.expenseCreated', { actor, amount: details?.amount || newVal?.amount ? formatMoney(details?.amount || newVal?.amount) : '' });
      case 'expense_updated': return t('audit.summary.expenseUpdated', { actor });
      case 'expense_deleted': return t('audit.summary.expenseDeleted', { actor });
      case 'expense_category_created': return t('audit.summary.expenseCategoryCreated', { actor, name: itemName || '' });
      case 'expense_category_updated': return t('audit.summary.expenseCategoryUpdated', { actor, name: itemName || '' });
      case 'expense_category_deleted': return t('audit.summary.expenseCategoryDeleted', { actor, name: itemName || '' });
      case 'purchase_created': return t('audit.summary.purchaseCreated', { actor, amount: details?.total_amount || newVal?.total_amount ? formatMoney(details?.total_amount || newVal?.total_amount) : '' });
      case 'purchase_updated': return t('audit.summary.purchaseUpdated', { actor });
      case 'purchase_deleted': return t('audit.summary.purchaseDeleted', { actor });
      case 'supplier_created': return t('audit.summary.supplierCreated', { actor, name: itemName || details?.supplier_name || '' });
      case 'supplier_updated': return t('audit.summary.supplierUpdated', { actor, name: itemName || details?.supplier_name || '' });
      case 'supplier_deleted': return t('audit.summary.supplierDeleted', { actor, name: itemName || details?.supplier_name || '' });
      case 'store_data_updated': return t('audit.summary.storeDataUpdated', { actor });
      case 'user_created': return t('audit.summary.userCreated', { actor, name: getUserAuditName(log) });
      case 'user_updated': return getUserUpdatedSummary(log, actor);
      case 'user_deleted': return t('audit.summary.userDeleted', { actor, name: getUserAuditName(log) });
      case 'user_refund_code_renewed': return t('audit.summary.userRefundCodeRenewed', { actor, name: formatJSON(log.details)?.target_name || getUserAuditName(log) });
      default: return t('audit.summary.default', { actor, action: getActionLabel(log.action) });
    }
  };

  const getReadableSummaryWithoutActor = (log) => {
    const details = formatJSON(log.details);
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    const itemName = details?.name || details?.product_name || newVal?.name || oldVal?.name || '';
    const formatMoney = (val) => `${t('currency.egp')} ${Number(val).toFixed(2)}`;
    switch (log.action) {
      case 'product_created': return t('audit.summaryNoActor.productCreated', { name: itemName || t('common.product') });
      case 'product_updated': return t('audit.summaryNoActor.productUpdated', { name: itemName || t('common.product') });
      case 'product_deleted': return t('audit.summaryNoActor.productDeleted', { name: itemName || t('common.product') });
      case 'session_opened': return t('audit.summaryNoActor.sessionOpened', { amount: details?.opening_cash || newVal?.opening_cash ? formatMoney(details?.opening_cash || newVal?.opening_cash) : '' });
      case 'session_closed': return t('audit.summaryNoActor.sessionClosed', { sales: details?.total_sales || newVal?.total_sales ? formatMoney(details?.total_sales || newVal?.total_sales) : '' });
      case 'session_force_closed': return t('audit.summaryNoActor.sessionForceClosed');
      case 'order_refunded': return t('audit.summaryNoActor.orderRefunded', { amount: details?.amount || details?.refund_amount ? formatMoney(details?.amount || details?.refund_amount) : '' });
      case 'order_partially_refunded': return t('audit.summaryNoActor.orderPartiallyRefunded', { amount: details?.amount || details?.refund_amount ? formatMoney(details?.amount || details?.refund_amount) : '' });
      case 'category_created': return t('audit.summaryNoActor.categoryCreated', { name: itemName || t('common.category') });
      case 'category_updated': return t('audit.summaryNoActor.categoryUpdated', { name: itemName || t('common.category') });
      case 'category_deleted': return t('audit.summaryNoActor.categoryDeleted', { name: itemName || t('common.category') });
      case 'offer_created': return t('audit.summaryNoActor.offerCreated', { name: itemName || t('nav.offers') });
      case 'offer_updated': return t('audit.summaryNoActor.offerUpdated', { name: itemName || t('nav.offers') });
      case 'offer_deleted': return t('audit.summaryNoActor.offerDeleted', { name: itemName || t('nav.offers') });
      case 'expense_created': return t('audit.summaryNoActor.expenseCreated', { amount: details?.amount || newVal?.amount ? formatMoney(details?.amount || newVal?.amount) : '' });
      case 'expense_updated': return t('audit.summaryNoActor.expenseUpdated');
      case 'expense_deleted': return t('audit.summaryNoActor.expenseDeleted');
      case 'expense_category_created': return t('audit.summaryNoActor.expenseCategoryCreated', { name: itemName || '' });
      case 'expense_category_updated': return t('audit.summaryNoActor.expenseCategoryUpdated', { name: itemName || '' });
      case 'expense_category_deleted': return t('audit.summaryNoActor.expenseCategoryDeleted', { name: itemName || '' });
      case 'purchase_created': return t('audit.summaryNoActor.purchaseCreated', { amount: details?.total_amount || newVal?.total_amount ? formatMoney(details?.total_amount || newVal?.total_amount) : '' });
      case 'purchase_updated': return t('audit.summaryNoActor.purchaseUpdated');
      case 'purchase_deleted': return t('audit.summaryNoActor.purchaseDeleted');
      case 'supplier_created': return t('audit.summaryNoActor.supplierCreated', { name: itemName || details?.supplier_name || '' });
      case 'supplier_updated': return t('audit.summaryNoActor.supplierUpdated', { name: itemName || details?.supplier_name || '' });
      case 'supplier_deleted': return t('audit.summaryNoActor.supplierDeleted', { name: itemName || details?.supplier_name || '' });
      case 'store_data_updated': return t('audit.summaryNoActor.storeDataUpdated');
      case 'user_created': return t('audit.summaryNoActor.userCreated', { name: getUserAuditName(log) });
      case 'user_updated': return getUserUpdatedSummaryNoActor(log);
      case 'user_deleted': return t('audit.summaryNoActor.userDeleted', { name: getUserAuditName(log) });
      case 'user_refund_code_renewed': return t('audit.summaryNoActor.userRefundCodeRenewed', { name: formatJSON(log.details)?.target_name || getUserAuditName(log) });
      default: return getActionLabel(log.action);
    }
  };

  const getSimpleChanges = (oldVal, newVal) => {
    const old = formatJSON(oldVal);
    const newV = formatJSON(newVal);
    if (!old && !newV) return [];
    const changes = [];
    const allKeys = new Set([...(old ? Object.keys(old) : []), ...(newV ? Object.keys(newV) : [])]);
    allKeys.forEach((key) => {
      if (hiddenFields.includes(key)) return;
      const oldValue = old?.[key];
      const newValue = newV?.[key];
      if (!valuesEqual(oldValue, newValue, key)) {
        const type = classifyFieldChange(oldValue, newValue, old, newV, key);
        changes.push({
          key,
          type,
          oldValue: CALENDAR_DATE_AUDIT_FIELDS.has(key) ? toCalendarDateDisplay(oldValue) : oldValue,
          newValue: CALENDAR_DATE_AUDIT_FIELDS.has(key) ? toCalendarDateDisplay(newValue) : newValue,
          label: getFieldLabel(key),
        });
      }
    });
    return changes;
  };

  const renderChangeArrow = () => (
    <span style={{ color: tk.textSecondary, flexShrink: 0 }}>{isRTL ? '←' : '→'}</span>
  );

  const ColorSwatch = ({ hex, size = 20, strikethrough = false }) => {
    const normalized = normalizeHexColor(hex) || '#9CA3AF';
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          ...(strikethrough ? { textDecoration: 'line-through', opacity: 0.8 } : {}),
        }}
      >
        <span
          style={{
            width: size,
            height: size,
            borderRadius: 6,
            background: normalized,
            border: `1px solid ${tk.border}`,
            flexShrink: 0,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
          }}
          title={normalized}
        />
        <span
          style={{
            fontFamily: '"JetBrains Mono",monospace',
            fontSize: 12,
            color: strikethrough ? tk.dangerText : tk.text,
          }}
        >
          {normalized}
        </span>
      </span>
    );
  };

  const fmtPurchaseMoney = (v) =>
    v != null && v !== '' && !Number.isNaN(Number(v)) ? `${t('currency.egp')} ${Number(v).toFixed(2)}` : '—';
  const fmtPurchaseQty = (v) =>
    v != null && v !== '' && !Number.isNaN(Number(v))
      ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })
      : '—';

  const renderFieldValue = (key, value, isInline = false) => {
    if (key === 'color') {
      const hex = normalizeHexColor(value);
      if (hex) return <ColorSwatch hex={hex} />;
    }
    if (CALENDAR_DATE_AUDIT_FIELDS.has(key)) {
      return <span style={{ color: tk.text }}>{formatCalendarDateDisplay(value)}</span>;
    }
    if (PURCHASE_MONEY_FIELDS.has(key)) {
      return <span style={{ color: tk.text, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseMoney(value)}</span>;
    }
    if (PURCHASE_QTY_FIELDS.has(key)) {
      return (
        <span style={{ color: tk.text, fontFamily: '"JetBrains Mono",monospace', direction: 'ltr', unicodeBidi: 'isolate', display: 'inline-block' }}>
          {fmtPurchaseQty(value)}
        </span>
      );
    }
    if (key === 'stock_quantity') {
      if (value === null || value === undefined || value === '') {
        return <span style={{ color: tk.textMuted, fontStyle: 'italic' }}>—</span>;
      }
      return (
        <span style={{ direction: 'ltr', unicodeBidi: 'isolate', display: 'inline-block' }}>
          {typeof value === 'number' ? value.toLocaleString() : String(value)}
        </span>
      );
    }
    return renderValue(value, isInline);
  };

  const isCategoryAction = (action) =>
    ['category_created', 'category_updated', 'category_deleted'].includes(action);

  const isOfferAction = (action) =>
    ['offer_created', 'offer_updated', 'offer_deleted'].includes(action);

  const OFFER_SCALAR_KEYS = ['name', 'sku', 'selling_price', 'is_active', 'image_path', 'preview_cost'];

  const pickOfferScalars = (offer) => {
    if (!offer) return null;
    const picked = {};
    OFFER_SCALAR_KEYS.forEach((key) => {
      if (offer[key] !== undefined) picked[key] = offer[key];
    });
    return picked;
  };

  const normalizeOfferItemsForCompare = (items) => {
    const list = Array.isArray(items) ? items : [];
    return list
      .map((item) => ({ product_id: String(item.product_id), quantity: Number(item.quantity) }))
      .sort((a, b) => a.product_id.localeCompare(b.product_id));
  };

  const offerItemsEqual = (oldItems, newItems) =>
    JSON.stringify(normalizeOfferItemsForCompare(oldItems)) === JSON.stringify(normalizeOfferItemsForCompare(newItems));

  const renderOfferComponentsTable = (items, { extended = false } = {}) => {
    const list = Array.isArray(items) ? items : [];
    if (!list.length) {
      return (
        <p style={{ fontSize: 12, color: tk.textMuted, margin: 0 }}>
          {t('offers.noComponents')}
        </p>
      );
    }
    const headers = extended
      ? [t('inventory.productName'), t('audit.fields.sku'), t('common.quantity'), t('inventory.unitType')]
      : [t('inventory.productName'), t('common.quantity'), t('inventory.unitType')];
    return (
      <div style={{ background: tk.surface, borderRadius: 8, border: `1px solid ${tk.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}` }}>
              {headers.map((header, idx) => (
                <th
                  key={header}
                  style={{
                    padding: '8px 10px',
                    textAlign: idx > (extended ? 1 : 0) ? 'right' : 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: tk.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((item, idx) => (
              <tr key={item.id || item.product_id || idx} style={{ borderBottom: `1px solid ${tk.border}` }}>
                <td style={{ padding: '8px 10px', color: tk.text }}>{item.product_name || '—'}</td>
                {extended && (
                  <td style={{ padding: '8px 10px', color: tk.textSecondary, fontFamily: '"JetBrains Mono",monospace' }}>
                    {item.sku != null && String(item.sku).trim() !== '' ? String(item.sku).trim() : '—'}
                  </td>
                )}
                <td style={{ padding: '8px 10px', textAlign: 'right', color: tk.textSecondary, fontFamily: '"JetBrains Mono",monospace' }}>
                  {fmtPurchaseQty(item.quantity)}
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: tk.textSecondary }}>
                  {formatProductUnitType(item.unit_type, t)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderOfferFullDetails = (offer, { deleted = false } = {}) => {
    if (!offer) return null;
    const name = offer.name || '—';
    const sku = offer.sku != null && String(offer.sku).trim() !== '' ? String(offer.sku).trim() : '—';
    const imagePath = offer.image_path != null && String(offer.image_path).trim() !== ''
      ? String(offer.image_path).trim()
      : '—';

    const DetailRow = ({ label, value, mono = false, highlight = false }) => (
      <div>
        <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
          {label}
        </div>
        <div style={{
          fontSize: 12,
          fontWeight: highlight ? 700 : 500,
          color: deleted && highlight ? tk.dangerText : tk.text,
          ...(deleted && highlight ? { textDecoration: 'line-through' } : {}),
          ...(mono ? { fontFamily: '"JetBrains Mono",monospace' } : {}),
          wordBreak: 'break-word',
        }}
        >
          {value}
        </div>
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 10,
            background: tk.surface,
            border: `1px solid ${tk.border}`,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <DetailRow label={t('offers.offerName')} value={name} highlight />
            <DetailRow label={t('audit.fields.sku')} value={sku} mono />
            <DetailRow label={t('inventory.sellingPrice')} value={fmtPurchaseMoney(offer.selling_price)} mono />
            <DetailRow label={t('offers.previewCost')} value={fmtPurchaseMoney(offer.preview_cost)} mono />
            <div>
              <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
                {t('audit.fields.is_active')}
              </div>
              {renderActiveStatusBadge(offer.is_active)}
            </div>
            <DetailRow label={t('inventory.image')} value={imagePath} mono />
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {t('offers.components')}
          </div>
          {renderOfferComponentsTable(offer.items, { extended: true })}
        </div>
      </div>
    );
  };

  const renderOfferUpdatedDiff = (oldVal, newVal) => {
    const scalarDiff = renderSimpleDiffView(pickOfferScalars(oldVal), pickOfferScalars(newVal));
    const itemsChanged = !offerItemsEqual(oldVal?.items, newVal?.items);

    if (!scalarDiff && !itemsChanged) {
      return (
        <div style={{ textAlign: 'center', padding: '8px 0', color: tk.textMuted, fontSize: 12 }}>
          {t('audit.noChangesDetected')}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {scalarDiff}
        {itemsChanged && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('offers.components')}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: tk.textSecondary, marginBottom: 6 }}>
                {t('audit.offerAudit.before')}
              </div>
              {renderOfferComponentsTable(oldVal?.items)}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: tk.textSecondary, marginBottom: 6 }}>
                {t('audit.offerAudit.after')}
              </div>
              {renderOfferComponentsTable(newVal?.items)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOfferAuditView = (log) => {
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    const action = log.action;

    if (action === 'offer_created') {
      if (!newVal) return null;
      return renderOfferFullDetails(newVal);
    }

    if (action === 'offer_deleted') {
      if (!oldVal) return null;
      return renderOfferFullDetails(oldVal, { deleted: true });
    }

    if (action === 'offer_updated') {
      if (!oldVal && !newVal) return null;
      return renderOfferUpdatedDiff(oldVal, newVal);
    }

    return null;
  };

  const isSessionAuditAction = (action) =>
    ['session_opened', 'session_closed', 'session_force_closed'].includes(action);

  const renderSessionAuditView = (log) => {
    const details = formatJSON(log.details) || {};
    const newVal = formatJSON(log.new_value);
    const openingCash = details.opening_cash ?? newVal?.opening_cash;
    const sessionNumber = details.session_number ?? newVal?.session_number;
    const registerName = details.register_name;
    const slotId = details.license_slot_id;
    const notes = details.notes;
    if (sessionNumber == null && !registerName && !slotId && openingCash == null && !notes) return null;

    const Row = ({ label, value, mono = false }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 12, color: tk.textSecondary, flexShrink: 0 }}>{label}</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: tk.text,
            textAlign: 'right',
            ...(mono ? { fontFamily: '"JetBrains Mono",monospace' } : {}),
          }}
        >
          {value}
        </span>
      </div>
    );

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '12px 14px',
          borderRadius: 10,
          background: tk.surface,
          border: `1px solid ${tk.border}`,
        }}
      >
        {sessionNumber != null && sessionNumber !== '' && (
          <Row label={t('sessions.sessionNo')} value={`#${sessionNumber}`} mono />
        )}
        {registerName && <Row label={t('sessions.register')} value={registerName} />}
        {slotId && <Row label={t('settings.licenseSlotsColSlot')} value={slotId} mono />}
        {log.action === 'session_opened' && openingCash != null && openingCash !== '' && (
          <Row
            label={t('sessions.openingCash')}
            value={`${t('currency.egp')} ${Number(openingCash).toFixed(2)}`}
            mono
          />
        )}
        {log.action === 'session_force_closed' && notes != null && String(notes).trim() !== '' && (
          <Row label={t('common.notes')} value={String(notes).trim()} />
        )}
      </div>
    );
  };

  const renderCategoryAuditView = (log) => {
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    const name = newVal?.name || oldVal?.name || '';
    const colorHex = newVal?.color ?? oldVal?.color;
    if (!name && !colorHex) return null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          marginBottom: 12,
          borderRadius: 10,
          background: tk.surface,
          border: `1px solid ${tk.border}`,
        }}
      >
        {colorHex && (
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: normalizeHexColor(colorHex) || colorHex,
              border: `1px solid ${tk.border}`,
              flexShrink: 0,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
            }}
            title={normalizeHexColor(colorHex) || String(colorHex)}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          {name && (
            <div style={{ fontSize: 14, fontWeight: 700, color: tk.text }}>{name}</div>
          )}
          {colorHex && (
            <div style={{ marginTop: name ? 4 : 0 }}>
              <ColorSwatch hex={colorHex} size={16} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStoreCollectionChange = (change) => {
    if (change.type === 'removed') {
      const messageKey = getStoreRemovedMessageKey(change.key, change.oldValue);
      if (messageKey) {
        const summary = getStoreFieldSummary(change.key, change.oldValue);
        return (
          <p style={{ fontSize: 12, color: tk.dangerText, margin: 0 }}>
            {t(messageKey, summary ? { value: summary } : undefined)}
          </p>
        );
      }
    }
    if (change.type === 'added') {
      const messageKey = getStoreAddedMessageKey(change.key, change.newValue);
      if (messageKey) {
        const summary = getStoreFieldSummary(change.key, change.newValue);
        return (
          <p style={{ fontSize: 12, color: tk.successText, margin: 0 }}>
            {t(messageKey, summary ? { value: summary } : undefined)}
          </p>
        );
      }
    }
    return null;
  };

  const formatReceiptFlag = (value) => (value ? t('common.yes') : t('common.no'));

  const formatStoreAuditFieldValue = (fieldKey, value) => formatStoreAuditScalarValue(fieldKey, value, { t });

  const renderStoreCollectionItemDiff = (change) => {
    if (!STORE_ITEM_COLLECTION_FIELDS.has(change.key)) return null;

    const itemChanges = getStoreCollectionItemChanges(change);
    if (itemChanges.length === 0) return null;

    const itemTypeMeta = {
      item_added: { icon: '+', bg: tk.successSoft, color: tk.successText, label: t('audit.storeChange.itemAdded') },
      item_removed: { icon: '−', bg: tk.dangerSoft, color: tk.dangerText, label: t('audit.storeChange.itemRemoved') },
      item_changed: { icon: '↔', bg: tk.accentSoft, color: tk.accentText, label: t('audit.storeChange.itemUpdated') },
    };

    const renderItemBody = (fieldKey, itemChange) => {
      const display = getStoreCollectionItemDisplay(fieldKey, itemChange);
      if (!display) return null;

      if (fieldKey === 'delivery_fee') {
        const fmtFee = (fee) => (fee != null && !Number.isNaN(fee) ? `${t('currency.egp')} ${Number(fee).toFixed(2)}` : '—');
        if (itemChange.type === 'item_changed') {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontWeight: 600, color: tk.text }}>{display.region}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
                <span style={{ color: tk.dangerText, textDecoration: 'line-through' }}>{fmtFee(display.oldFee)}</span>
                {renderChangeArrow()}
                <span style={{ color: tk.successText, fontWeight: 600 }}>{fmtFee(display.fee)}</span>
              </div>
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: tk.text }}>{display.region}</span>
            <span style={{ color: itemChange.type === 'item_removed' ? tk.dangerText : tk.successText, fontWeight: 600 }}>{fmtFee(display.fee)}</span>
          </div>
        );
      }

      if (fieldKey === 'phone_numbers') {
        if (itemChange.type === 'item_changed') {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono",monospace' }}>{display.number}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
                <span style={{ color: tk.textSecondary }}>{t('audit.storeChange.onReceipt')}:</span>
                <span style={{ color: tk.dangerText, textDecoration: 'line-through' }}>{formatReceiptFlag(display.oldIncludeInReceipt)}</span>
                {renderChangeArrow()}
                <span style={{ color: tk.successText, fontWeight: 600 }}>{formatReceiptFlag(display.includeInReceipt)}</span>
              </div>
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono",monospace' }}>{display.number}</span>
            <span style={{ fontSize: 11.5, color: tk.textSecondary }}>{t('audit.storeChange.onReceipt')}: {formatReceiptFlag(display.includeInReceipt)}</span>
          </div>
        );
      }

      if (fieldKey === 'social_media_urls') {
        if (itemChange.type === 'item_changed') {
          const urlChanged = display.oldUrl !== display.url;
          const receiptChanged = display.oldIncludeInReceipt !== display.includeInReceipt;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontWeight: 600, color: tk.text, textTransform: 'capitalize' }}>{display.platform}</span>
              {urlChanged && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <span style={{ color: tk.dangerText, textDecoration: 'line-through', wordBreak: 'break-all' }}>{display.oldUrl || '—'}</span>
                  {renderChangeArrow()}
                  <span style={{ color: tk.successText, fontWeight: 600, wordBreak: 'break-all' }}>{display.url || '—'}</span>
                </div>
              )}
              {receiptChanged && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <span style={{ color: tk.textSecondary }}>{t('audit.storeChange.onReceipt')}:</span>
                  <span style={{ color: tk.dangerText, textDecoration: 'line-through' }}>{formatReceiptFlag(display.oldIncludeInReceipt)}</span>
                  {renderChangeArrow()}
                  <span style={{ color: tk.successText, fontWeight: 600 }}>{formatReceiptFlag(display.includeInReceipt)}</span>
                </div>
              )}
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, color: tk.text, textTransform: 'capitalize' }}>{display.platform}</span>
            <span style={{ color: itemChange.type === 'item_removed' ? tk.dangerText : tk.text, wordBreak: 'break-all' }}>{display.url || '—'}</span>
            <span style={{ fontSize: 11.5, color: tk.textSecondary }}>{t('audit.storeChange.onReceipt')}: {formatReceiptFlag(display.includeInReceipt)}</span>
          </div>
        );
      }

      return null;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {itemChanges.map((itemChange, idx) => {
          const meta = itemTypeMeta[itemChange.type] || itemTypeMeta.item_changed;
          return (
            <div
              key={`${itemChange.type}-${itemChange.itemKey}-${idx}`}
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                background: tk.surfaceAlt,
                border: `1px solid ${tk.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 99, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, background: meta.bg, color: meta.color, flexShrink: 0,
                }}>
                  {meta.icon}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {meta.label}
                </span>
              </div>
              {renderItemBody(change.key, itemChange)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderValue = (value, isInline = false) => {
    if (value === null || value === undefined) return <span style={{ color: tk.textMuted, fontStyle: 'italic' }}>—</span>;
    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: tk.textMuted, fontStyle: 'italic' }}>{t('common.none')}</span>;
      return (
        <div>
          {value.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: 99, background: tk.accentText, marginTop: 5, flexShrink: 0 }} />
              <div>{renderValue(item, true)}</div>
            </div>
          ))}
        </div>
      );
    }
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return <span style={{ color: tk.textMuted, fontStyle: 'italic' }}>—</span>;
      return (
        <div style={isInline ? { display: 'inline' } : {}}>
          {keys.map((key) => (
            <div key={key} style={{ display: isInline ? 'inline-flex' : 'flex', alignItems: 'flex-start', gap: 4, marginRight: 8, marginBottom: 2 }}>
              <span style={{ color: tk.accentText, fontWeight: 500 }}>{getFieldLabel(key)}:</span>
              <div>{renderValue(value[key], true)}</div>
            </div>
          ))}
        </div>
      );
    }
    if (typeof value === 'boolean') return <span style={{ color: tk.accentText, fontWeight: 600 }}>{value ? t('common.yes') : t('common.no')}</span>;
    if (typeof value === 'number') return <span style={{ color: tk.successText, fontWeight: 600 }}>{value.toLocaleString()}</span>;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
        return <span style={{ color: tk.text }}>{formatCalendarDateDisplay(value)}</span>;
      }
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return <span style={{ color: tk.text }}>{formatCalendarDateDisplay(value)}</span>;
      }
      return <span style={{ color: tk.text }}>{value}</span>;
    }
    return <span style={{ color: tk.text }}>{String(value)}</span>;
  };

  const renderSimpleDiffView = (oldVal, newVal) => {
    const changes = getSimpleChanges(oldVal, newVal);
    if (changes.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {changes.map((change, idx) => (
          <div key={idx} style={{ padding: 12, borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 10, background: tk.surfaceAlt }}>
            <div style={{ width: 24, height: 24, borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13, fontWeight: 700, background: change.type === 'added' ? tk.successSoft : change.type === 'removed' ? tk.dangerSoft : tk.accentSoft, color: change.type === 'added' ? tk.successText : change.type === 'removed' ? tk.dangerText : tk.accentText }}>
              {change.type === 'added' ? '+' : change.type === 'removed' ? '−' : '↔'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 12.5, color: tk.text, marginBottom: 4 }}>{change.label}</div>
              {renderStoreCollectionChange(change) || renderStoreCollectionItemDiff(change) || (
                <>
              {change.type === 'added' && <div style={{ fontSize: 12, color: tk.successText }}>{t('audit.setTo')} {renderFieldValue(change.key, change.newValue, true)}</div>}
              {change.type === 'removed' && <div style={{ fontSize: 12, color: tk.dangerText, textDecoration: 'line-through' }}>{renderFieldValue(change.key, change.oldValue, true)}</div>}
              {change.type === 'changed' && (
                <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <span style={{ color: tk.dangerText, textDecoration: change.key === 'color' ? 'none' : 'line-through' }}>
                    {change.key === 'color' ? <ColorSwatch hex={change.oldValue} strikethrough /> : renderFieldValue(change.key, change.oldValue, true)}
                  </span>
                  {renderChangeArrow()}
                  <span style={{ color: tk.successText }}>{renderFieldValue(change.key, change.newValue, true)}</span>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const isStoreAction = (action) => action === 'store_data_updated';

  const renderStoreChangeBody = (change) => {
    if (isStoreCollectionField(change.key)) {
      const itemDiff = renderStoreCollectionItemDiff(change);
      if (itemDiff) return itemDiff;
      const collectionMsg = renderStoreCollectionChange(change);
      if (collectionMsg) return collectionMsg;
    }

    if (change.type === 'added') {
      return (
        <div style={{ fontSize: 12.5, color: tk.successText, fontWeight: 600 }}>
          {formatStoreAuditFieldValue(change.key, change.newValue)}
        </div>
      );
    }
    if (change.type === 'removed') {
      return (
        <div style={{ fontSize: 12.5, color: tk.dangerText, fontWeight: 600, textDecoration: 'line-through' }}>
          {formatStoreAuditFieldValue(change.key, change.oldValue)}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
        <span style={{ color: tk.dangerText, textDecoration: 'line-through' }}>{formatStoreAuditFieldValue(change.key, change.oldValue)}</span>
        {renderChangeArrow()}
        <span style={{ color: tk.successText, fontWeight: 600 }}>{formatStoreAuditFieldValue(change.key, change.newValue)}</span>
      </div>
    );
  };

  const renderStoreAuditView = (log) => {
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    const changes = getSimpleChanges(log.old_value, log.new_value);
    if (changes.length === 0) return null;

    const storeName = newVal?.name || oldVal?.name || t('settings.storeInformation');

    return (
      <div style={{ background: tk.surface, borderRadius: 10, border: `1px solid ${tk.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${tk.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: tk.text }}>{storeName}</div>
            <div style={{ fontSize: 12, color: tk.textMuted, marginTop: 4 }}>{t('settings.storeInformationSubtitle')}</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: tk.accentSoft, color: tk.accentText, whiteSpace: 'nowrap' }}>
            {t('audit.storeAudit.settingsUpdated')}
          </span>
        </div>

        <div>
          <div style={{ padding: '10px 16px 0', fontSize: 10, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('audit.storeAudit.changes')}
          </div>
          {changes.map((change, idx) => (
            <div key={`${change.key}-${idx}`} style={{ padding: '12px 14px', borderBottom: idx < changes.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
              <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                {change.label}
              </div>
              {renderStoreChangeBody(change)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const isPurchaseAction = (action) =>
    ['purchase_created', 'purchase_updated', 'purchase_deleted'].includes(action);

  const isUserAction = (action) =>
    ['user_created', 'user_updated', 'user_deleted'].includes(action);

  const renderUserAuditView = (log) => {
    const details = formatJSON(log.details) || {};
    const oldVal = formatJSON(log.old_value);
    const newVal = formatJSON(log.new_value);
    const displayName = details.full_name || details.username || newVal?.full_name || newVal?.username || oldVal?.full_name || oldVal?.username || '—';
    const username = details.username || newVal?.username || oldVal?.username || '';
    const role = details.role || newVal?.role || oldVal?.role;
    const event = details.event || (log.action === 'user_created' ? 'created' : log.action === 'user_deleted' ? 'deleted' : 'updated');
    const changes = Array.isArray(details.changes) ? details.changes : [];

    const eventLabel = {
      created: t('audit.userAudit.accountCreated'),
      updated: t('audit.userAudit.accountUpdated'),
      deactivated: t('audit.userAudit.accountDeactivated'),
      deleted: t('audit.userAudit.accountDeleted'),
    }[event] || t('audit.userAudit.accountUpdated');

    const eventColor = event === 'deleted' || event === 'deactivated' ? tk.dangerText : event === 'created' ? tk.successText : tk.warning;

    const InfoRow = ({ label, value }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px solid ${tk.border}` }}>
        <span style={{ fontSize: 12, color: tk.textSecondary }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: tk.text, textAlign: 'right' }}>{value}</span>
      </div>
    );

    const ChangeRow = ({ change }) => (
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${tk.border}` }}>
        <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
          {getFieldLabel(change.field === 'password_changed' ? 'password_changed' : change.field)}
        </div>
        {change.field === 'password_changed' ? (
          <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.warning }}>{t('audit.userAudit.passwordReset')}</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
            <span style={{ color: tk.dangerText, textDecoration: 'line-through' }}>{formatUserAuditFieldValue(change.field, change.from)}</span>
            {renderChangeArrow()}
            <span style={{ color: tk.successText, fontWeight: 600 }}>{formatUserAuditFieldValue(change.field, change.to)}</span>
          </div>
        )}
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ background: tk.surface, borderRadius: 10, border: `1px solid ${tk.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${tk.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: tk.text }}>{displayName}</div>
              {username && (
                <div style={{ fontSize: 12, color: tk.textMuted, marginTop: 4, fontFamily: '"JetBrains Mono",monospace' }}>@{username}</div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              {role && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: tk.accentSoft, color: tk.accentText, textTransform: 'uppercase' }}>
                  {getRoleLabel(role)}
                </span>
              )}
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: event === 'deleted' || event === 'deactivated' ? tk.dangerSoft : event === 'created' ? tk.successSoft : tk.warningSoft, color: eventColor }}>
                {eventLabel}
              </span>
            </div>
          </div>

          {event === 'created' && (
            <div style={{ padding: '4px 16px 12px' }}>
              <InfoRow label={t('audit.fields.username')} value={username || '—'} />
              <InfoRow label={t('audit.fields.role')} value={getRoleLabel(details.role)} />
              <InfoRow label={t('audit.fields.status')} value={formatUserStatus(details.status)} />
            </div>
          )}

          {event === 'deleted' && (
            <div style={{ padding: '4px 16px 12px' }}>
              <InfoRow label={t('audit.fields.username')} value={username || '—'} />
              <InfoRow label={t('audit.fields.role')} value={getRoleLabel(details.role)} />
              <InfoRow label={t('audit.fields.status')} value={formatUserStatus(details.status)} />
            </div>
          )}

          {(event === 'updated' || event === 'deactivated') && changes.length > 0 && (
            <div>
              <div style={{ padding: '10px 16px 0', fontSize: 10, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('audit.userAudit.changes')}
              </div>
              {changes.map((change, idx) => (
                <ChangeRow key={`${change.field}-${idx}`} change={change} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const isNestedPurchaseAudit = (val) =>
    val && typeof val === 'object' && (val.product != null || Object.prototype.hasOwnProperty.call(val, 'purchase'));

  const renderPurchaseCreatedView = (oldVal, newVal) => {
    const product = newVal?.product || {};
    const purchase = newVal?.purchase || {};
    const oldProduct = oldVal?.product || {};

    const oldStock = oldProduct.stock_quantity ?? purchase.old_inventory;
    const newStock = product.stock_quantity;
    const delta = purchase.quantity;

    const StatCell = ({ label, value, color }) => (
      <div style={{ background: tk.surface, padding: '12px 14px' }}>
        <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: color || tk.text, fontFamily: '"JetBrains Mono",monospace' }}>{value}</div>
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ background: tk.surface, borderRadius: 10, border: `1px solid ${tk.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${tk.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: tk.text }}>{product.name || '—'}</div>
              <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
                {product.sku && (
                  <span style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono",monospace' }}>SKU: {product.sku}</span>
                )}
                {product.unit_type && (
                  <span style={{ fontSize: 11, color: tk.textMuted }}>· {product.unit_type}</span>
                )}
              </div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: tk.accentSoft, color: tk.accentText, whiteSpace: 'nowrap' }}>
              {t('audit.entityPurchase') || 'Purchase'}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: tk.border }}>
            <StatCell label={t('common.quantity') || 'Quantity'} value={fmtPurchaseQty(purchase.quantity)} />
            <StatCell label={t('inventory.costPrice') || 'Unit Price'} value={fmtPurchaseMoney(purchase.unit_price)} />
            <StatCell label={t('common.total') || 'Total'} value={fmtPurchaseMoney(purchase.total_price)} color={tk.accentText} />
          </div>

          {oldStock != null && newStock != null && (
            <div style={{ padding: '12px 14px', borderTop: `1px solid ${tk.border}` }}>
              <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                {t('inventory.stockQuantity') || 'Stock Change'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexDirection: 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
                <span style={{ padding: '4px 12px', borderRadius: 6, background: tk.sunken, color: tk.textSecondary, fontSize: 13, fontWeight: 600, fontFamily: '"JetBrains Mono",monospace' }}>
                  {fmtPurchaseQty(oldStock)}
                </span>
                {renderChangeArrow()}
                <span style={{ padding: '4px 12px', borderRadius: 6, background: tk.successSoft, color: tk.successText, fontSize: 13, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace' }}>
                  {fmtPurchaseQty(newStock)}
                </span>
                {delta != null && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: tk.successText }}>
                    +{fmtPurchaseQty(delta)}
                  </span>
                )}
              </div>
            </div>
          )}

          {(purchase.unit_selling_price != null || product.selling_price != null) && (
            <div style={{ padding: '10px 14px', borderTop: `1px solid ${tk.border}`, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {purchase.old_cost_price != null && (
                <div>
                  <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t('inventory.costPrice') || 'Cost Price'}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseMoney(purchase.old_cost_price)}</div>
                </div>
              )}
              {(purchase.unit_selling_price ?? product.selling_price) != null && (
                <div>
                  <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t('inventory.sellingPrice') || 'Selling Price'}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseMoney(purchase.unit_selling_price ?? product.selling_price)}</div>
                </div>
              )}
            </div>
          )}

          {purchase.purchased_at && (
            <div style={{ padding: '10px 14px', borderTop: `1px solid ${tk.border}`, display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: tk.textMuted }}>{t('common.date') || 'Date'}:</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: tk.text }}>
                {formatCalendarDateDisplay(purchase.purchased_at)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPurchaseFlatSummary = (purchase, { extended = false } = {}) => {
    if (!purchase) return null;
    return (
      <div style={{ background: tk.surface, borderRadius: 10, border: `1px solid ${tk.border}`, overflow: 'hidden', marginBottom: extended ? 0 : 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: tk.border }}>
          <div style={{ background: tk.surface, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{t('common.quantity')}</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseQty(purchase.quantity)}</div>
          </div>
          <div style={{ background: tk.surface, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{t('inventory.costPrice')}</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseMoney(purchase.unit_price)}</div>
          </div>
          <div style={{ background: tk.surface, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{t('common.total')}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: tk.accentText, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseMoney(purchase.total_price)}</div>
          </div>
        </div>
        {extended && (
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${tk.border}`, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {purchase.unit_selling_price != null && (
              <div>
                <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t('inventory.sellingPrice')}</div>
                <div style={{ fontSize: 12, fontWeight: 600, fontFamily: '"JetBrains Mono",monospace' }}>{fmtPurchaseMoney(purchase.unit_selling_price)}</div>
              </div>
            )}
            {purchase.purchased_at && (
              <div>
                <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t('common.date')}</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{formatCalendarDateDisplay(purchase.purchased_at)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPurchaseView = (log) => {
    const newVal = formatJSON(log.new_value);
    const oldVal = formatJSON(log.old_value);
    const action = log.action;

    if (action === 'purchase_created' && isNestedPurchaseAudit(newVal)) {
      return renderPurchaseCreatedView(oldVal, newVal);
    }

    if (action === 'purchase_deleted') {
      return renderPurchaseFlatSummary(oldVal, { extended: true });
    }

    if (action === 'purchase_updated') {
      const diff = renderSimpleDiffView(oldVal, newVal);
      return (
        <div>
          {renderPurchaseFlatSummary(newVal || oldVal)}
          {diff}
        </div>
      );
    }

    if (isNestedPurchaseAudit(newVal)) {
      return renderPurchaseCreatedView(oldVal, newVal);
    }

    return renderSimpleDiffView(oldVal, newVal);
  };

  const isRefundAction = (action) =>
    ['order_refunded', 'order_partially_refunded'].includes(action);

  const openPreview = async (log) => {
    setPreviewModal({ open: true, log });
    setRefundDetails(null);
    if (isRefundAction(log.action)) {
      const details = formatJSON(log.details);
      if (details && details.refund_id) {
        setLoadingRefund(true);
        try {
          const res = await apiService.getRefund(details.refund_id);
          setRefundDetails(res.data);
        } catch (err) {
          console.error('Failed to fetch refund details', err);
        } finally {
          setLoadingRefund(false);
        }
      }
    }
  };

  const closePreview = () => {
    setPreviewModal({ open: false, log: null });
    setRefundDetails(null);
  };

  const actionOptions = [
    { value: 'all', label: t('audit.allActions') },
    { value: 'order_refunded', label: t('audit.orderRefunded') },
    { value: 'order_partially_refunded', label: t('audit.orderPartiallyRefunded') },
    { value: 'product_created', label: t('audit.productCreated') },
    { value: 'product_updated', label: t('audit.productUpdated') },
    { value: 'product_deleted', label: t('audit.productDeleted') },
    { value: 'session_opened', label: t('audit.sessionOpened') },
    { value: 'session_closed', label: t('audit.sessionClosed') },
    { value: 'session_force_closed', label: t('audit.sessionForceClosed') },
    { value: 'category_created', label: t('audit.categoryCreated') },
    { value: 'category_updated', label: t('audit.categoryUpdated') },
    { value: 'category_deleted', label: t('audit.categoryDeleted') },
    { value: 'offer_created', label: t('audit.offerCreated') },
    { value: 'offer_updated', label: t('audit.offerUpdated') },
    { value: 'offer_deleted', label: t('audit.offerDeleted') },
    { value: 'expense_created', label: t('audit.expenseCreated') },
    { value: 'expense_updated', label: t('audit.expenseUpdated') },
    { value: 'expense_deleted', label: t('audit.expenseDeleted') },
    { value: 'expense_category_created', label: t('audit.expenseCategoryCreated') },
    { value: 'expense_category_updated', label: t('audit.expenseCategoryUpdated') },
    { value: 'expense_category_deleted', label: t('audit.expenseCategoryDeleted') },
    { value: 'purchase_created', label: t('audit.purchaseCreated') },
    { value: 'purchase_updated', label: t('audit.purchaseUpdated') },
    { value: 'purchase_deleted', label: t('audit.purchaseDeleted') },
    { value: 'supplier_created', label: t('audit.supplierCreated') },
    { value: 'supplier_updated', label: t('audit.supplierUpdated') },
    { value: 'supplier_deleted', label: t('audit.supplierDeleted') },
    { value: 'user_created', label: t('audit.userCreated') },
    { value: 'user_updated', label: t('audit.userUpdated') },
    { value: 'user_deleted', label: t('audit.userDeleted') },
    { value: 'user_refund_code_renewed', label: t('audit.userRefundCodeRenewed') },
    { value: 'store_data_updated', label: t('audit.storeDataUpdated') },
  ];

  const entityTypeOptions = [
    { value: 'all', label: t('audit.allTypes') },
    { value: 'product', label: t('audit.entityProduct') },
    { value: 'session', label: t('audit.entitySession') },
    { value: 'refund', label: t('audit.entityRefund') },
    { value: 'category', label: t('audit.entityCategory') },
    { value: 'expense', label: t('audit.entityExpense') },
    { value: 'expense_category', label: t('audit.entityExpenseCategory') },
    { value: 'purchase', label: t('audit.entityPurchase') },
    { value: 'supplier', label: t('audit.entitySupplier') },
    { value: 'store_data', label: t('audit.entityStoreData') },
    { value: 'user', label: t('audit.entityUser') },
  ];

  const actorOptions = [
    { value: 'all', label: t('audit.allActors') },
    ...(users || []).map((u) => ({ value: u.id, label: u.full_name })),
  ];

  const handleExport = async () => {
    if (!filteredList.length) return;
    setExporting(true);
    try {
      const labels = t('audit.excelExport');
      const L = typeof labels === 'object' && labels !== null ? labels : {};
      const today = new Date().toISOString().split('T')[0];
      const rowsForExcel = filteredList.map((e) => ({
        ...e,
        actor_role: getRoleLabel(e.actor_role),
        entity_type: getEntityTypeLabel(e.entity_type),
        action: getActionLabel(e.action),
      }));
      await exportAuditToExcel(rowsForExcel, `audit-log-${today}.xlsx`, L);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  // Group paginated events by calendar day
  const eventGroups = React.useMemo(() => {
    const groups = [];
    let lastKey = null;
    paginatedEvents.forEach((event) => {
      const ts = event.acted_at || event.created_at;
      const key = getDayKey(ts);
      if (key !== lastKey) {
        lastKey = key;
        groups.push({ type: 'header', ts, label: formatDayLabel(ts) });
      }
      groups.push({ type: 'event', event });
    });
    return groups;
  }, [paginatedEvents]);

  return (
    <div style={wrapperStyle} className={`theme-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Shimmer keyframes */}
      <style>{`
        @keyframes auditShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <PageHeader
        tk={tk}
        title={t('nav.audit')}
        subtitle={language === 'ar' ? 'كل إجراء يتم عبر النظام' : 'Every action across the system'}
        actions={
          <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={handleExport}>
            {exporting ? t('monthlyClosing.downloading') : t('audit.downloadExcel')}
          </GhostBtn>
        }
      />

      {/* ─── Two-row filter bar ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) auto', gap: 8, alignItems: 'end' }}>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={{ ...dateInput, width: '100%', height: 34, cursor: 'pointer' }}
          >
            {actionOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            style={{ ...dateInput, width: '100%', height: 34, cursor: 'pointer' }}
          >
            {entityTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            onFocus={(users || []).length === 0 ? () => dispatch(fetchUsers(true)) : undefined}
            style={{ ...dateInput, width: '100%', height: 34, cursor: 'pointer' }}
          >
            {actorOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ ...dateInput, width: '100%', height: 34 }} />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ ...dateInput, width: '100%', height: 34 }} />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '0 10px', height: 34,
                background: tk.dangerSoft, border: 'none',
                borderRadius: 8, color: tk.dangerText,
                cursor: 'pointer', fontSize: 11.5, fontFamily: 'inherit',
              }}
            >
              <X size={11} />
              {t('common.clear')}
            </button>
          )}
        </div>
      </div>

      {/* ─── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{ background: tk.dangerSoft, color: tk.dangerText, border: `1px solid ${tk.danger}33`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* ─── Event list ────────────────────────────────────────────────────── */}
      <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 12, overflow: 'hidden' }}>

        {/* Skeleton loading */}
        {loading && [0,1,2,3,4,5].map((i) => (
          <div key={i} style={{ borderBottom: i < 5 ? `1px solid ${tk.border}` : 'none' }}>
            <SkeletonRow tk={tk} />
          </div>
        ))}

        {/* Empty state */}
        {!loading && paginatedEvents.length === 0 && (
          <div style={{ padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: tk.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color={tk.textMuted} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{t('audit.noAuditLogs') || 'No audit events found'}</div>
            <div style={{ fontSize: 12, color: tk.textMuted }}>Try adjusting your filters or date range</div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{ marginTop: 4, padding: '6px 14px', borderRadius: 7, background: tk.accentSoft, border: 'none', color: tk.accentText, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500 }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Event rows with day grouping */}
        {!loading && eventGroups.map((item, i) => {
          if (item.type === 'header') {
            return (
              <div
                key={`hd-${i}`}
                style={{
                  padding: '7px 16px',
                  background: tk.surfaceAlt,
                  borderBottom: `1px solid ${tk.border}`,
                  borderTop: i > 0 ? `1px solid ${tk.border}` : 'none',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {item.label}
                </span>
                <span style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono",monospace' }}>
                  {item.ts ? new Date(item.ts).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                </span>
              </div>
            );
          }

          const event = item.event;
          const isLast = i === eventGroups.length - 1 || eventGroups[i + 1]?.type === 'header';
          const role = event.actor_role || event.role || event.user_role || 'system';
          const roleBg = role === 'owner' ? '#ede9fe' : role === 'admin' ? tk.warningSoft : role === 'system' ? tk.sunken : tk.accentSoft;
          const roleColor = role === 'owner' ? '#5b21b6' : role === 'admin' ? tk.warning : role === 'system' ? tk.textMuted : tk.accentText;
          const iconColor = getKindColor(event.action || '', tk);

          return (
            <div
              key={event.id || i}
              onClick={() => openPreview(event)}
              style={{
                display: 'grid',
                gridTemplateColumns: '52px 32px 1fr auto 32px',
                alignItems: 'center',
                gap: '0 12px',
                padding: '11px 16px',
                borderBottom: isLast ? 'none' : `1px solid ${tk.border}`,
                cursor: 'pointer',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = tk.surfaceAlt; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Col 1 — time */}
              <span style={{
                fontFamily: '"JetBrains Mono",monospace',
                fontSize: 11,
                color: tk.textMuted,
                letterSpacing: 0.2,
                textAlign: isRTL ? 'right' : 'left',
              }}>
                {formatTime(event.acted_at || event.created_at)}
              </span>

              {/* Col 2 — kind icon */}
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: tk.surfaceAlt,
                border: `1px solid ${tk.border}`,
                color: iconColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {getKindIcon(event.action || '')}
              </div>

              {/* Col 3 — main content */}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: tk.text, whiteSpace: 'nowrap' }}>
                    {event.actor_name || t('common.system')}
                  </span>
                  <span style={{
                    fontSize: 9.5, fontWeight: 700,
                    padding: '1px 5px', borderRadius: 3,
                    background: roleBg, color: roleColor,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    flexShrink: 0,
                  }}>
                    {role}
                  </span>
                  <span style={{ fontSize: 12, color: tk.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getActionLabel(event.action)}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: tk.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getReadableSummaryWithoutActor(event)}
                </div>
              </div>

              {/* Col 4 — entity chip (list view, no UUID exposure) */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                {event.entity_type && (
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    padding: '3px 8px', borderRadius: 5,
                    background: tk.accentSoft, color: tk.accentText,
                    fontFamily: '"JetBrains Mono",monospace',
                    whiteSpace: 'nowrap',
                  }}>
                    {getEntityTypeLabel(event.entity_type)}
                  </span>
                )}
              </div>

              {/* Col 5 — eye button */}
              <button
                onClick={(e) => { e.stopPropagation(); openPreview(event); }}
                style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                  color: tk.textSecondary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'background 0.1s, color 0.1s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = tk.border; e.currentTarget.style.color = tk.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = tk.surfaceAlt; e.currentTarget.style.color = tk.textSecondary; }}
              >
                <Eye size={13} />
              </button>
            </div>
          );
        })}
      </div>

      <Pagination tk={tk} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* ─── Preview Modal ─────────────────────────────────────────────────── */}
      {previewModal.open && previewModal.log && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, maxWidth: 720, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${tk.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: getKindColor(previewModal.log.action, tk), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {getKindIcon(previewModal.log.action)}
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: tk.text, lineHeight: 1.3 }}>{t('audit.details')}</div>
                  <div style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono",monospace', marginTop: 1 }}>
                    {formatDateTime(previewModal.log.acted_at || previewModal.log.created_at)}
                  </div>
                </div>
              </div>
              <button
                onClick={closePreview}
                style={{ width: 30, height: 30, borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* Summary */}
              <div style={{ padding: '12px 14px', borderRadius: 10, background: tk.surfaceAlt, border: `1px solid ${tk.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: tk.text, lineHeight: 1.6 }}>
                  {getReadableSummary(previewModal.log)}
                </div>
                {previewModal.log.actor_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <span style={{ fontSize: 11, color: tk.textMuted }}>{t('common.by')}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: tk.text }}>{previewModal.log.actor_name}</span>
                    {(() => {
                      const r = previewModal.log.actor_role || previewModal.log.role || previewModal.log.user_role || 'system';
                      const rb = r === 'owner' ? '#ede9fe' : r === 'admin' ? tk.warningSoft : r === 'system' ? tk.sunken : tk.accentSoft;
                      const rc = r === 'owner' ? '#5b21b6' : r === 'admin' ? tk.warning : r === 'system' ? tk.textMuted : tk.accentText;
                      return (
                        <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: rb, color: rc, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {r}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Refund details */}
              {isRefundAction(previewModal.log.action) && (
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{t('refunds.refundDetails')}</div>
                  {loadingRefund ? (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('common.loading')}</div>
                  ) : refundDetails ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, background: tk.surface, borderRadius: 8, padding: 12, border: `1px solid ${tk.border}` }}>
                        {[
                          { label: t('refunds.refundNo'), val: <span style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 11 }}>{refundDetails.id}</span> },
                          { label: t('reports.receiptNo'), val: refundDetails.receipt_num || '—' },
                          { label: t('refunds.refundAmount'), val: <span style={{ color: tk.dangerText, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace' }}>{t('currency.egp')} {formatCurrency(refundDetails.amount)}</span> },
                          { label: t('common.total'), val: `${t('currency.egp')} ${formatCurrency(refundDetails.order_total)}` },
                          { label: t('refunds.approvedBy'), val: refundDetails.approver_name || '—' },
                          { label: t('common.date'), val: refundDetails.created_at ? new Date(refundDetails.created_at).toLocaleString() : '—' },
                        ].map(({ label, val }, idx) => (
                          <div key={idx}>
                            <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                            <div style={{ fontSize: 12.5, color: tk.text }}>{val}</div>
                          </div>
                        ))}
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t('refunds.refundReason')}</div>
                          <div style={{ fontSize: 12.5, color: tk.text }}>{refundDetails.reason || '—'}</div>
                        </div>
                      </div>

                      {refundDetails.items && refundDetails.items.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('refunds.refundedItems')}</div>
                          <div style={{ background: tk.surface, borderRadius: 8, border: `1px solid ${tk.border}`, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead>
                                <tr style={{ background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}` }}>
                                  {[t('inventory.productName'), t('common.quantity'), t('refunds.refundQuantity'), t('refunds.refundAmount'), t('refunds.restock')].map((h, i) => (
                                    <th key={i} style={{ padding: '8px 10px', textAlign: i > 0 ? 'right' : 'left', fontSize: 10, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {refundDetails.items.map((item) => (
                                  <tr key={item.id} style={{ borderBottom: `1px solid ${tk.border}` }}>
                                    <td style={{ padding: '8px 10px', color: tk.text }}>{item.product_name || t('inventory.uncategorized')}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', color: tk.textSecondary }}>{parseFloat(item.original_quantity || 0).toLocaleString()}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', color: tk.warning, fontWeight: 600 }}>{parseFloat(item.refunded_quantity).toLocaleString()}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', color: tk.dangerText, fontWeight: 600 }}>{t('currency.egp')} {formatCurrency(item.refunded_amount)}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                      <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: item.restock ? tk.successSoft : tk.sunken, color: item.restock ? tk.successText : tk.textMuted }}>
                                        {item.restock ? t('common.yes') : t('common.no')}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('common.noData')}</div>
                  )}
                </div>
              )}

              {/* Additional details (non-refund) */}
              {isSessionAuditAction(previewModal.log.action) && renderSessionAuditView(previewModal.log)}

              {previewModal.log.details && !isRefundAction(previewModal.log.action) && !isUserAction(previewModal.log.action) && !isSessionAuditAction(previewModal.log.action) && (() => {
                const details = formatJSON(previewModal.log.details) || {};
                const visibleEntries = Object.entries(details).filter(([key]) => !hiddenFields.includes(key) && key !== 'changes' && key !== 'event');
                if (visibleEntries.length === 0) return null;
                return (
                  <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{t('audit.additionalInfo')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 8 }}>
                      {visibleEntries.map(([key, value]) => (
                        <React.Fragment key={key}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: tk.textSecondary, paddingTop: 1 }}>{getFieldLabel(key)}</span>
                          <span style={{ fontSize: 12, color: tk.text }}>{renderValue(value, true)}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {isUserAction(previewModal.log.action) && (
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                  {renderUserAuditView(previewModal.log)}
                </div>
              )}

              {isStoreAction(previewModal.log.action) && (previewModal.log.old_value || previewModal.log.new_value) && (
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                  {renderStoreAuditView(previewModal.log) || (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('audit.noChangesDetected')}</div>
                  )}
                </div>
              )}

              {/* What changed */}
              {!isUserAction(previewModal.log.action) && !isStoreAction(previewModal.log.action) && (previewModal.log.old_value || previewModal.log.new_value) && (
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>{t('audit.whatChanged')}</div>
                  {isPurchaseAction(previewModal.log.action) ? (
                    renderPurchaseView(previewModal.log) || (
                      <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('audit.noChangesDetected')}</div>
                    )
                  ) : isOfferAction(previewModal.log.action) ? (
                    renderOfferAuditView(previewModal.log) || (
                      <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('audit.noChangesDetected')}</div>
                    )
                  ) : (
                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                      {isCategoryAction(previewModal.log.action) && renderCategoryAuditView(previewModal.log)}
                      {renderSimpleDiffView(previewModal.log.old_value, previewModal.log.new_value) || (
                        <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('audit.noChangesDetected')}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isUserAction(previewModal.log.action) && !isStoreAction(previewModal.log.action) && !previewModal.log.details && !previewModal.log.old_value && !previewModal.log.new_value && (
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, textAlign: 'center', color: tk.textMuted, fontSize: 12, border: `1px solid ${tk.border}` }}>
                  {t('common.noData')}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${tk.border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={closePreview}
                style={{ padding: '0 16px', height: 34, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500 }}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audit;
